// PayoutDetail — admin-only drill-down for one payout.
//
// Route: /payouts/:payoutId, gated by RequireAuth role="admin" in
// router.tsx. The backend is the authoritative gate
// (RequireRole(admin) on GET /v1/admin/payouts/:id); the router check
// just spares a merchant user a 403 round-trip if they hand-type the
// URL.
//
// Surfaces the audit trail with the full `before`/`after` jsonb diff
// on each state transition — which is exactly the "error or payload
// from the provider" the operator needs to triage a failed row. This
// is why the page is admin-only: those jsonb columns hold raw upstream
// PSP responses (IDFC Initiate / CheckStatus / webhook body) that
// merchants must never see.
//
// Money handling (CLAUDE.md rule 6): amount_minor is bigint INR paise,
// rendered via fmtCcyMinor.
import { useState } from "react";
import axios from "axios";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { I } from "@/components/Icons";
import { Modal, StatusPill } from "@/components/UI";
import { fmtCcyMinor, formatDateTime } from "@/utils/format";
import { providerLabel } from "@/utils/providerLabel";
import { useAdminPayout } from "@/queries/useAdminPayout";
import {
  cancelPayout,
  cancelBlockedMessage,
  type CancelBlockedReason,
} from "@/api/payouts";
import { apiErrorMessage } from "@/utils/apiError";
import { useUI } from "@/store/ui";

export function PayoutDetail() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { payoutId: pidParam } = useParams<{ payoutId: string }>();

  // Defensive parse — NaN-on-malformed lets the useAdminPayout hook's
  // `enabled` gate keep the fetch from firing on /payouts/abc. The page
  // then renders the "Invalid payout id" branch.
  const payoutId =
    pidParam != null && /^\d+$/.test(pidParam) ? Number(pidParam) : null;

  const { data, isLoading, isError, error, refetch, isFetching } =
    useAdminPayout(payoutId);

  const addToast = useUI((st) => st.addToast);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  if (payoutId == null) {
    return (
      <div className="page">
        <Link to="/payouts" className="btn ghost sm">
          <I.ChevronLeft size={12} /> Back to payouts
        </Link>
        <h1 style={{ marginTop: 16 }}>Payout</h1>
        <div className="card flat">
          <div className="empty">
            <p>Invalid payout id in the URL.</p>
            <button
              className="btn"
              type="button"
              onClick={() => navigate("/payouts")}
              style={{ marginTop: 12 }}
            >
              Back to payouts
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="page">
        <Link to="/payouts" className="btn ghost sm">
          <I.ChevronLeft size={12} /> Back to payouts
        </Link>
        <div className="empty" style={{ marginTop: 24 }}>
          <div className="dim" style={{ fontSize: 13 }}>
            Loading payout…
          </div>
        </div>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="page">
        <Link to="/payouts" className="btn ghost sm">
          <I.ChevronLeft size={12} /> Back to payouts
        </Link>
        <h1 style={{ marginTop: 16 }}>Payout not found</h1>
        <p className="dim">
          No payout with id {payoutId}, or it was removed.
          {error instanceof Error ? ` (${error.message})` : ""}
        </p>
      </div>
    );
  }

  const { payout: p, merchant, bank_account, beneficiary, audit_trail } = data;

  // Whether cancelling would work is decided by the BACKEND (payout.can_cancel)
  // and never re-derived here. The rule turns on the rail type and on whether
  // an export has claimed the row; a second copy in TypeScript would drift the
  // first time a rail is added, and the way it would drift is a Cancel button
  // that looks live on money already moving.
  const canCancel = p.can_cancel ?? false;
  const blockedWhy = cancelBlockedMessage(p.cancel_blocked_reason, p.export_id);
  // Already succeeded or failed. Nothing left to stop, and overwriting a
  // settled row would replace what happened with what someone intended.
  const isTerminal = p.status === "success" || p.status === "failure";

  async function runCancel() {
    if (payoutId == null) return;
    const reason = cancelReason.trim();
    if (reason === "") {
      setCancelError("Give a reason — it is stored on the payout and in the audit log.");
      return;
    }
    setCancelling(true);
    setCancelError(null);
    try {
      // force is sent exactly when the backend told us the safe path would
      // refuse. The dialog has already said what that means.
      await cancelPayout(payoutId, reason, !canCancel);
      void qc.invalidateQueries({ queryKey: ["admin-payout", payoutId] });
      void qc.invalidateQueries({ queryKey: ["payouts"] });
      setCancelOpen(false);
      setCancelReason("");
      addToast({ title: `Payout pay_${payoutId} cancelled`, body: reason });
    } catch (err) {
      // A 409 here means the row moved while the dialog was open — approved,
      // dispatched, or claimed by an export. Surface the backend's own reason
      // rather than a generic failure, because each one asks the operator for
      // something different.
      const code = extractCancelReason(err);
      setCancelError(
        code
          ? cancelBlockedMessage(code, extractExportId(err))
          : apiErrorMessage(err, "The payout could not be cancelled."),
      );
      // Re-read: whatever it became, the page should now show it.
      void qc.invalidateQueries({ queryKey: ["admin-payout", payoutId] });
    } finally {
      setCancelling(false);
    }
  }

  return (
    <div className="page">
      <Link to="/payouts" className="btn ghost sm">
        <I.ChevronLeft size={12} /> Back to payouts
      </Link>

      <div className="page-head" style={{ marginTop: 12 }}>
        <div>
          <div className="eyebrow">pay_{p.id}</div>
          <h1>{fmtCcyMinor(p.amount_minor, p.currency)}</h1>
          <div className="sub">
            <span className="mono">{p.request_id}</span> · created{" "}
            {formatDateTime(p.created_at)}
            {merchant && (
              <>
                {" · "}
                <Link to={`/merchants/${merchant.slug || merchant.id}`}>
                  {merchant.display_name}
                </Link>
              </>
            )}
          </div>
        </div>
        <div className="actions" style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <StatusPill status={p.status} />
          <button
            className="btn ghost sm"
            type="button"
            onClick={() => {
              // Drop the cached entry first so refetch doesn't return
              // the stale row while the network is in flight.
              void qc.invalidateQueries({ queryKey: ["admin-payout", payoutId] });
              void refetch();
            }}
            disabled={isLoading}
            title="Refresh"
          >
            <I.Refresh
              size={12}
              style={{
                animation: isFetching
                  ? "spin 800ms linear infinite"
                  : undefined,
              }}
            />{" "}
            Refresh
          </button>
          {/* Cancel — shown only when the backend says it would work. A
              disabled button with a tooltip would be worse than no button:
              the reason it cannot be cancelled is a sentence, not a hover. */}
          {/* The button is on the page for every payout that has not already
              finished. Whether the stop is clean or an override is decided in
              the dialog, not by hiding the control: an operator who has
              decided a payout must stop needs somewhere for that decision to
              live, and a missing button just moves the write to psql where
              nothing is audited. Terminal rows have no decision left to
              record, so they get no button. */}
          {!isTerminal && (
            <button
              className="btn danger sm"
              type="button"
              onClick={() => {
                setCancelReason("");
                setCancelError(null);
                setCancelOpen(true);
              }}
              title={
                canCancel
                  ? "Stop this payout. It has not left the system."
                  : "Record this payout as cancelled. It is already at the rail — read the warning before confirming."
              }
            >
              <I.X size={11} /> Cancel payout
            </button>
          )}
        </div>
      </div>

      {cancelOpen && (
        <Modal
          open
          title={`Cancel pay_${p.id}?`}
          onClose={() => setCancelOpen(false)}
          foot={
            <>
              <button
                className="btn ghost"
                type="button"
                onClick={() => setCancelOpen(false)}
                disabled={cancelling}
              >
                Keep it
              </button>
              <button
                className="btn danger"
                type="button"
                onClick={() => void runCancel()}
                disabled={cancelling}
              >
                {cancelling
                  ? "Cancelling…"
                  : canCancel
                    ? "Cancel payout"
                    : "Mark cancelled anyway"}
              </button>
            </>
          }
        >
          <div className="stack-12">
            {canCancel ? (
              <>
                <p style={{ margin: 0, fontSize: 13 }}>
                  {fmtCcyMinor(p.amount_minor, p.currency)} to{" "}
                  {beneficiary?.name ?? "this beneficiary"} will be stopped and
                  marked failed. No money has left the system, so nothing needs
                  to be recovered.
                </p>
                <p className="dim" style={{ margin: 0, fontSize: 12 }}>
                  This cannot be undone — a cancelled payout has to be
                  re-submitted as a new one.
                </p>
              </>
            ) : (
              <>
                {/* The override. The warning is the point of this branch: the
                    operator is about to change our record of a payment that
                    is already out of our hands, and the one thing they must
                    not walk away believing is that this stopped the money. */}
                <div
                  style={{
                    border: "1px solid var(--neg)",
                    borderRadius: 8,
                    padding: 12,
                    background: "color-mix(in oklch, var(--neg) 6%, transparent)",
                  }}
                >
                  <p style={{ margin: 0, fontSize: 13, fontWeight: 600 }}>
                    This will not stop the money.
                  </p>
                  <p style={{ margin: "6px 0 0", fontSize: 13 }}>
                    {blockedWhy}
                  </p>
                  <p style={{ margin: "6px 0 0", fontSize: 13 }}>
                    Cancelling here marks{" "}
                    {fmtCcyMinor(p.amount_minor, p.currency)} to{" "}
                    {beneficiary?.name ?? "this beneficiary"} as cancelled in
                    our records only. The payment may still go out.
                  </p>
                </div>
                <p className="dim" style={{ margin: 0, fontSize: 12 }}>
                  Use this to record a decision you have already taken
                  elsewhere — after asking the bank to stop it, or once you
                  know it will not be sent. The merchant&rsquo;s funds stay
                  held and flagged for reconciliation rather than released,
                  because the real outcome is unknown until the rail reports
                  it.
                  {p.provider_txn_id && (
                    <>
                      {" "}
                      Quote{" "}
                      <span className="mono">{p.provider_txn_id}</span> to the
                      bank.
                    </>
                  )}
                </p>
              </>
            )}
            <div className="field">
              <label className="label" htmlFor="cancel-reason">
                Reason
              </label>
              <input
                id="cancel-reason"
                className="input"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder={
                  canCancel
                    ? "Stored on the payout and in the audit log"
                    : "Why this is being recorded as cancelled"
                }
                maxLength={500}
                autoFocus
                autoComplete="off"
              />
            </div>
            {cancelError && (
              <p className="hint neg" style={{ fontSize: 12 }}>
                {cancelError}
              </p>
            )}
          </div>
        </Modal>
      )}

      {/* ── Core facts grid ─────────────────────────────────────── */}
      <div className="card flat">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: 16,
            padding: 16,
          }}
        >
          <InfoCell label="Mode" value={p.txn_mode === 1 ? "IMPS" : p.txn_mode === 2 ? "NEFT" : String(p.txn_mode)} />
          <InfoCell
            label="Bank rail"
            value={
              bank_account ? (
                <>
                  <div>{bank_account.label}</div>
                  <div className="dim" style={{ fontSize: 11 }}>
                    {providerLabel(bank_account.provider)} · ba_{bank_account.id}
                  </div>
                </>
              ) : (
                "—"
              )
            }
          />
          <InfoCell
            label="Beneficiary"
            value={
              beneficiary ? (
                <>
                  <Link to={`/beneficiaries/${beneficiary.id}`}>
                    {beneficiary.name}
                  </Link>
                  <div className="dim" style={{ fontSize: 11 }}>
                    {beneficiary.ifsc} · {beneficiary.account_no}
                  </div>
                </>
              ) : (
                "—"
              )
            }
          />
          <InfoCell
            label="Provider txn"
            value={p.provider_txn_id ?? "—"}
            mono
          />
          <InfoCell label="UTR" value={p.utr_no ?? "—"} mono />
          {p.batch_id != null && (
            <InfoCell
              label="Batch"
              value={
                <Link to={`/payouts/batches/${p.batch_id}`} className="mono">
                  batch_{p.batch_id}
                </Link>
              }
            />
          )}
        </div>
      </div>

      {/* ── Failure-reason banner (only when failed) ────────────── */}
      {p.status === "failure" && (p.error_code || p.error_message) && (
        <div
          className="card flat"
          style={{
            marginTop: 12,
            background: "var(--neg-soft, #fef3f2)",
            borderColor: "var(--neg, #b42318)",
          }}
        >
          <div style={{ padding: 16 }}>
            <div
              className="eyebrow"
              style={{
                fontSize: 10,
                letterSpacing: "0.06em",
                color: "var(--neg, #b42318)",
                marginBottom: 6,
              }}
            >
              Failure reason
            </div>
            {p.error_code && (
              <div className="mono" style={{ fontSize: 13, marginBottom: 4 }}>
                {p.error_code}
              </div>
            )}
            {p.error_message && (
              <div style={{ fontSize: 13, color: "var(--ink-1)" }}>
                {p.error_message}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Audit trail ─────────────────────────────────────────── */}
      <div className="card flat" style={{ marginTop: 12 }}>
        <div className="card-head">
          <h3>Provider audit trail</h3>
          <div className="spacer" />
          <span className="dim" style={{ fontSize: 11 }}>
            {audit_trail.length} {audit_trail.length === 1 ? "entry" : "entries"}
          </span>
        </div>
        {audit_trail.length === 0 ? (
          <div className="empty">
            <p>
              No audit entries recorded for this payout yet. Approval +
              dispatch entries land here once the row leaves
              <span className="mono"> needs_approval</span>.
            </p>
          </div>
        ) : (
          <div style={{ padding: "0 16px 16px" }}>
            {audit_trail.map((entry) => (
              <AuditEntryCard key={entry.id} entry={entry} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── InfoCell — labeled value cell shared with BeneficiaryDetail ─────────────

function InfoCell({
  label,
  value,
  mono,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div>
      <div
        className="eyebrow"
        style={{ fontSize: 10, letterSpacing: "0.06em", marginBottom: 4 }}
      >
        {label}
      </div>
      <div className={mono ? "mono" : undefined} style={{ fontSize: 14 }}>
        {value}
      </div>
    </div>
  );
}

// ─── Audit entry card ────────────────────────────────────────────────────────

interface AuditEntry {
  id: number;
  actor_user_id: number | null;
  actor_role: string | null;
  action: string;
  before: unknown;
  after: unknown;
  request_id: string | null;
  ip: string | null;
  created_at: string;
}

// AuditEntryCard — one row in the trail. Closed by default per operator
// feedback: open trails read as a wall of JSON; the action+timestamp
// summary is what 80% of triage needs. Click the row to expand, which
// reveals two views of the diff:
//
//   • Parsed (default) — known fields lifted from before/after into a
//     human-readable key/value table. Highlights what CHANGED, formats
//     amount_minor as INR currency, and surfaces provider error_code +
//     error_message inline so the operator doesn't have to grep the raw
//     payload.
//
//   • Raw JSON — the unmodified jsonb columns, pretty-printed. Available
//     for entries with non-standard shapes (webhook bodies, beneficiary
//     mutations) or when the operator wants to copy a literal value into
//     a support ticket.
function AuditEntryCard({ entry }: { entry: AuditEntry }) {
  const [expanded, setExpanded] = useState(false);
  const [view, setView] = useState<"parsed" | "raw">("parsed");

  const before = jsonForDisplay(entry.before);
  const after = jsonForDisplay(entry.after);
  const hasBefore = before !== null;
  const hasAfter = after !== null;
  const hasDiff = hasBefore || hasAfter;

  // Surface the upstream classification at the row level so the operator
  // sees terminal-status entries at a glance without expanding.
  const summary = entrySummary(entry);

  return (
    <div
      style={{
        borderTop: "1px solid var(--hair)",
      }}
    >
      <button
        type="button"
        onClick={() => hasDiff && setExpanded((v) => !v)}
        aria-expanded={expanded}
        // Keep the click target visually identical to the previous static
        // row — nothing about the chrome changes, only the cursor + the
        // toggle indicator.
        style={{
          all: "unset",
          display: "flex",
          alignItems: "baseline",
          gap: 12,
          flexWrap: "wrap",
          padding: "12px 0",
          width: "100%",
          cursor: hasDiff ? "pointer" : "default",
        }}
      >
        {hasDiff && (
          <span
            className="dim"
            aria-hidden="true"
            style={{
              fontSize: 10,
              fontFamily: "var(--font-mono, monospace)",
              minWidth: 10,
              display: "inline-block",
              transform: expanded ? "rotate(90deg)" : undefined,
              transition: "transform 80ms ease-out",
            }}
          >
            ▸
          </span>
        )}
        <span className="mono" style={{ fontSize: 13, fontWeight: 500 }}>
          {entry.action}
        </span>
        <span className="dim" style={{ fontSize: 11 }}>
          {formatDateTime(entry.created_at)}
        </span>
        {entry.actor_role && (
          <span className="dim" style={{ fontSize: 11 }}>
            · {entry.actor_role}
            {entry.actor_user_id != null && <> #{entry.actor_user_id}</>}
          </span>
        )}
        {entry.ip && (
          <span className="dim mono" style={{ fontSize: 11 }}>
            · {entry.ip}
          </span>
        )}
        {summary && (
          <span
            className="dim"
            style={{ fontSize: 11, fontStyle: "italic" }}
          >
            · {summary}
          </span>
        )}
      </button>

      {expanded && hasDiff && (
        <div style={{ paddingBottom: 12 }}>
          {/* View tabs — single-row, low-chrome. The pair stays inline so
              the operator doesn't need to leave the trail to flip view. */}
          <div
            role="tablist"
            aria-label="Audit entry view"
            style={{
              display: "flex",
              gap: 4,
              marginBottom: 8,
              fontSize: 11,
            }}
          >
            <ViewTab
              label="Parsed"
              active={view === "parsed"}
              onClick={() => setView("parsed")}
            />
            <ViewTab
              label="Raw JSON"
              active={view === "raw"}
              onClick={() => setView("raw")}
            />
          </div>

          {view === "parsed" ? (
            <ParsedDiff before={entry.before} after={entry.after} />
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns:
                  hasBefore && hasAfter ? "1fr 1fr" : "1fr",
                gap: 12,
              }}
            >
              {hasBefore && <JsonBlock label="Before" json={before!} />}
              {hasAfter && <JsonBlock label="After" json={after!} />}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ViewTab({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      style={{
        all: "unset",
        cursor: "pointer",
        padding: "4px 10px",
        borderRadius: 4,
        border: "1px solid var(--hair)",
        background: active ? "var(--accent-soft, #eef)" : "transparent",
        color: active ? "var(--accent, #339)" : "var(--ink-2)",
        fontWeight: active ? 500 : 400,
      }}
    >
      {label}
    </button>
  );
}

function JsonBlock({ label, json }: { label: string; json: string }) {
  return (
    <div>
      <div
        className="eyebrow"
        style={{ fontSize: 10, letterSpacing: "0.06em", marginBottom: 4 }}
      >
        {label}
      </div>
      <pre
        className="mono"
        style={{
          fontSize: 11,
          background: "var(--bg-sunk, #f7f7f8)",
          border: "1px solid var(--hair)",
          borderRadius: 4,
          padding: 8,
          overflow: "auto",
          maxHeight: 240,
          margin: 0,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {json}
      </pre>
    </div>
  );
}

// ─── Parsed-view rendering ───────────────────────────────────────────────────

// Stable display order for known audit-payload keys. Fields not listed
// here render below these in the order they appear in the payload —
// known fields stay pinned at the top, everything else lands in a tail.
const KNOWN_KEYS = [
  "status",
  "amount_minor",
  "currency",
  "txn_mode",
  "provider",
  "provider_txn_id",
  "utr_no",
  "error_code",
  "error_message",
  "request_id",
  "merchant_id",
  "bank_account_id",
  "beneficiary_id",
  "verified_at",
  "name",
  "account_no",
  "ifsc",
  "email",
  "phone",
  "created_at",
  "updated_at",
] as const;

const TXN_MODE_LABEL: Record<number, string> = { 1: "IMPS", 2: "NEFT" };

function entrySummary(entry: AuditEntry): string {
  // Single-line summary of the most useful "after" field for this action.
  // Falls back to "" when nothing instructive is present.
  const after = entry.after as Record<string, unknown> | null | undefined;
  if (!after || typeof after !== "object") return "";
  const status = typeof after.status === "string" ? after.status : "";
  const errCode = typeof after.error_code === "string" ? after.error_code : "";
  if (status && errCode) return `${status} · ${errCode}`;
  if (status) return status;
  if (errCode) return errCode;
  return "";
}

// ParsedDiff renders a key/value diff of the known payout fields between
// the before/after snapshots. Three buckets, in order:
//   1) Changed — value differs between before and after (highlighted)
//   2) Set — value present in after but absent / null in before
//   3) Unchanged — value present in both with the same content (collapsed)
function ParsedDiff({ before, after }: { before: unknown; after: unknown }) {
  const bMap = toObjectMap(before);
  const aMap = toObjectMap(after);

  // If "after" is the only side populated (initiate / verify entries),
  // treat every after-field as "set". Otherwise compute a real diff.
  const keys = orderedUnion(bMap, aMap);

  type RowKind = "changed" | "set" | "unchanged" | "cleared";
  const rows: Array<{
    key: string;
    kind: RowKind;
    before: unknown;
    after: unknown;
  }> = [];
  for (const k of keys) {
    const bv = bMap[k];
    const av = aMap[k];
    const bHas = k in bMap && bv != null && bv !== "";
    const aHas = k in aMap && av != null && av !== "";
    let kind: RowKind;
    if (bHas && aHas) {
      kind = jsonStable(bv) === jsonStable(av) ? "unchanged" : "changed";
    } else if (!bHas && aHas) {
      kind = "set";
    } else if (bHas && !aHas) {
      kind = "cleared";
    } else {
      // both empty — skip entirely
      continue;
    }
    rows.push({ key: k, kind, before: bv, after: av });
  }

  if (rows.length === 0) {
    return (
      <div className="dim" style={{ fontSize: 11 }}>
        No human-readable fields in this entry — switch to Raw JSON.
      </div>
    );
  }

  return (
    <div
      style={{
        border: "1px solid var(--hair)",
        borderRadius: 4,
        overflow: "hidden",
        fontSize: 12,
      }}
    >
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.key}
              style={{
                borderTop: "1px solid var(--hair)",
              }}
            >
              <td
                className="mono dim"
                style={{
                  padding: "6px 10px",
                  width: "30%",
                  verticalAlign: "top",
                  fontSize: 11,
                }}
              >
                {r.key}
                {r.kind === "changed" && (
                  <span
                    style={{
                      marginLeft: 6,
                      color: "var(--accent, #339)",
                      fontStyle: "italic",
                    }}
                  >
                    changed
                  </span>
                )}
                {r.kind === "set" && (
                  <span
                    style={{
                      marginLeft: 6,
                      color: "var(--good, #2a7)",
                      fontStyle: "italic",
                    }}
                  >
                    set
                  </span>
                )}
                {r.kind === "cleared" && (
                  <span
                    style={{
                      marginLeft: 6,
                      color: "var(--bad, #c33)",
                      fontStyle: "italic",
                    }}
                  >
                    cleared
                  </span>
                )}
              </td>
              <td style={{ padding: "6px 10px", verticalAlign: "top" }}>
                {r.kind === "changed" ? (
                  <>
                    <div
                      className="mono"
                      style={{ color: "var(--bad, #c33)", fontSize: 11 }}
                    >
                      − {renderValue(r.key, r.before)}
                    </div>
                    <div
                      className="mono"
                      style={{ color: "var(--good, #2a7)", fontSize: 11 }}
                    >
                      + {renderValue(r.key, r.after)}
                    </div>
                  </>
                ) : r.kind === "cleared" ? (
                  <span
                    className="mono"
                    style={{ color: "var(--bad, #c33)", fontSize: 11 }}
                  >
                    − {renderValue(r.key, r.before)}
                  </span>
                ) : (
                  <span className="mono" style={{ fontSize: 11 }}>
                    {renderValue(r.key, r.after ?? r.before)}
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function toObjectMap(v: unknown): Record<string, unknown> {
  if (v == null || typeof v !== "object" || Array.isArray(v)) return {};
  return v as Record<string, unknown>;
}

// orderedUnion returns the union of keys from both maps, with KNOWN_KEYS
// surfaced first in their fixed order, then the remainder alphabetised.
function orderedUnion(
  a: Record<string, unknown>,
  b: Record<string, unknown>,
): string[] {
  const all = new Set<string>([...Object.keys(a), ...Object.keys(b)]);
  const known: string[] = [];
  for (const k of KNOWN_KEYS) {
    if (all.has(k)) {
      known.push(k);
      all.delete(k);
    }
  }
  const tail = Array.from(all).sort();
  return [...known, ...tail];
}

// jsonStable — deterministic comparison string for diff-equality.
// JSON.stringify is order-dependent for object keys (insertion order in
// modern JS), but for the audit payload's flat shape that's fine for
// equality.
function jsonStable(v: unknown): string {
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

// renderValue formats a known field's value for display. amount_minor
// becomes "₹1,500.00"; txn_mode becomes "IMPS"/"NEFT"; ISO timestamps
// become "May 26, 2026 13:42"; everything else stringifies.
function renderValue(key: string, raw: unknown): string {
  if (raw == null || raw === "") return "—";
  if (key === "amount_minor") {
    // amount_minor arrives as either a number (small values) or string
    // (BigInt-tagged from the codec on the wire). Normalise to BigInt
    // for safe formatting — never parseFloat (CLAUDE.md rule 6).
    try {
      const minor = typeof raw === "string" ? BigInt(raw) : BigInt(String(raw));
      return fmtCcyMinor(minor, "INR");
    } catch {
      return String(raw);
    }
  }
  if (key === "txn_mode" && typeof raw === "number") {
    return TXN_MODE_LABEL[raw] ?? String(raw);
  }
  if (
    (key === "created_at" ||
      key === "updated_at" ||
      key === "verified_at") &&
    typeof raw === "string"
  ) {
    return formatDateTime(raw);
  }
  if (typeof raw === "string" || typeof raw === "number" || typeof raw === "boolean") {
    return String(raw);
  }
  // Nested object/array — show its JSON inline at a smaller scale.
  // Parsed view doesn't recurse; the operator can flip to Raw for that.
  try {
    return JSON.stringify(raw);
  } catch {
    return String(raw);
  }
}

// jsonForDisplay returns a JSON-pretty string or null for empty bodies.
// "null" / undefined / empty-object payloads collapse to null so the
// caller hides the block entirely — keeps the trail dense for entries
// that didn't carry a diff.
function jsonForDisplay(value: unknown): string | null {
  if (value == null) return null;
  if (typeof value === "object" && Object.keys(value as object).length === 0) {
    return null;
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    // Cyclic or non-serialisable — render the typeof + a sentinel so the
    // operator can at least tell the entry carried data.
    return `[unserialisable ${typeof value}]`;
  }
}

// ─── 409 payload helpers ─────────────────────────────────────────────────────
//
// A cancel that loses a race comes back as 409 payout_not_cancellable carrying
// the reason the row is now in. Pulling it out lets the dialog say "an export
// claimed this while you were typing" instead of "request failed", which is
// the difference between an operator knowing what to do next and filing a bug.

function errBody(err: unknown): Record<string, unknown> | null {
  if (axios.isAxiosError(err) && err.response?.data && typeof err.response.data === "object") {
    return err.response.data as Record<string, unknown>;
  }
  return null;
}

function extractCancelReason(err: unknown): CancelBlockedReason | undefined {
  const body = errBody(err);
  if (body?.code !== "payout_not_cancellable") return undefined;
  const reason = body.reason;
  return typeof reason === "string" ? (reason as CancelBlockedReason) : undefined;
}

function extractExportId(err: unknown): number | undefined {
  const id = errBody(err)?.export_id;
  return typeof id === "number" ? id : undefined;
}
